/**
 * Cmd+K / Ctrl+K student search palette.
 *
 * Mounts once at app boot, listens for the global shortcut, and renders
 * a modal with a fuzzy-ish filter over window.nccd.students. Enter
 * navigates to the selected student.
 */
import { navigate } from '../router.js';
import { getVisibleStudents } from '../auth.js';

import { setHtml } from '../utils/dom-set.js';

import { esc } from '../utils/dom.js';
// Archived (graduated) students are lazy-loaded once and folded into the
// search pool so a student who left in a past year is still findable.
let _archivedCache = null;
let _archivedLoading = false;
async function ensureArchivedLoaded() {
  if (_archivedCache !== null || _archivedLoading) return;
  _archivedLoading = true;
  try {
    const { loadArchivedStudents } = await import('../services/students.js');
    _archivedCache = await loadArchivedStudents();
  } catch { _archivedCache = []; }
  _archivedLoading = false;
  if (palette && inputEl) renderList();
}

function visibleStudents() {
  // Scope BOTH active and archived students to what the user may see. Archived
  // (graduated) students were previously concatenated raw, so a scope-restricted
  // user could surface students outside their scope via Cmd+K. getVisibleStudents
  // short-circuits to everything for leadership/admin, so no change for them.
  const active = getVisibleStudents(window.nccd?.students || []);
  const archived = getVisibleStudents(_archivedCache || []);
  return active.concat(archived);
}
let palette = null;
let listEl = null;
let inputEl = null;
let activeIdx = 0;
let filtered = [];

function fuzzyMatch(haystack, needle) {
  if (!needle) return true;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase().trim();
  if (h.includes(n)) return true;
  // initials match: "hh" -> "Harry Hain"
  const parts = h.split(/\s+/);
  const initials = parts.map(p => p[0] || '').join('');
  return initials.includes(n);
}

function renderList() {
  const q = (inputEl?.value || '').trim();
  // Only surface students the current user is allowed to see — no leaking
  // names into Cmd+K results even when search is broad.
  const all = visibleStudents();
  filtered = all.filter(s => {
    const hay = [s.name, s.parentName, s.parent2Name, s.teacherName, s.esoName].filter(Boolean).join(' ');
    return fuzzyMatch(hay, q);
  }).slice(0, 30);
  if (activeIdx >= filtered.length) activeIdx = 0;
  if (!filtered.length) {
    setHtml(listEl, '<div class="search-palette-empty">No students match.</div>');
    return;
  }
  setHtml(listEl, filtered.map((s, i) => `
    <div class="search-result ${i === activeIdx ? 'active' : ''}" data-idx="${i}" data-id="${s.id}">
      ${s.photo ? `<img src="${esc(s.photo)}" class="search-result-photo" alt="">` : '<div class="search-result-photo-placeholder"></div>'}
      <div class="search-result-body">
        <div class="search-result-name">${esc(s.name || 'Unnamed')}</div>
        <div class="search-result-meta">${esc(s.teacherName || s.parentName || '')}</div>
      </div>
      ${s._archived ? '<span class="badge badge-status-na search-result-badge">graduated</span>' : s.leftSchool ? '<span class="badge badge-status-na search-result-badge">left</span>' : (s.nccdStatus ? `<span class="badge badge-status-${s.nccdStatus} search-result-badge">${esc(s.nccdStatus)}</span>` : '')}
    </div>
  `).join(''));
  listEl.querySelectorAll('.search-result').forEach(el => {
    el.addEventListener('mouseover', () => { activeIdx = parseInt(el.dataset.idx); renderList(); });
    el.addEventListener('click', () => goToSelected());
  });
}

function goToSelected() {
  const s = filtered[activeIdx];
  if (!s) return;
  closePalette();
  // Graduated students live in /archive (read-only). They aren't in the active
  // records, so show a read-only info card rather than opening the dialog.
  if (s._archived) { showArchivedInfo(s); return; }
  // Open the same modal "card" a row-click gives. If we're already on the
  // records page, open it directly (navigating to #/records wouldn't fire a
  // hashchange). Otherwise stash the intent and route to records, which
  // picks it up via window._openStudentOnRecords on render.
  const onRecords = (window.location.hash || '').startsWith('#/records');
  if (onRecords && typeof window._openStudentPanel === 'function') {
    window._openStudentPanel(s.id, 'summary');
  } else {
    window._openStudentOnRecords = { id: s.id, tab: 'summary' };
    navigate('/records');
  }
}

function _fmtArchivedDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
    return d.toLocaleDateString('en-AU');
  } catch { return ''; }
}

function showArchivedInfo(s) {
  const ov = document.createElement('div');
  ov.className = 'confirm-overlay';
  const dx = Array.isArray(s.diagnoses) ? s.diagnoses.join(', ') : '';
  ov.innerHTML = `<div class="confirm-dialog confirm-dialog-narrow search-archived-card">
    <h3>${esc(s.name || 'Student')} <span class="badge badge-status-na">graduated</span></h3>
    <p class="text-muted">This student has graduated / left and their record is archived - read-only, kept for the 75-year retention period.</p>
    <div class="search-archived-facts">
      ${s.yearLevel ? `<div><strong>Final year level:</strong> ${esc(s.yearLevel)}</div>` : ''}
      ${dx ? `<div><strong>Diagnoses:</strong> ${esc(dx)}</div>` : ''}
      ${s.archivedAt ? `<div><strong>Archived:</strong> ${esc(_fmtArchivedDate(s.archivedAt))}</div>` : ''}
    </div>
    <div class="confirm-actions"><button class="btn btn-primary search-archived-close">Close</button></div>
  </div>`;
  document.body.appendChild(ov);
  ov.addEventListener('click', (e) => { if (e.target === ov || e.target.closest('.search-archived-close')) ov.remove(); });
}

function openPalette() {
  if (palette) return;
  palette = document.createElement('div');
  palette.className = 'search-palette-overlay';
  setHtml(palette, `
    <div class="search-palette">
      <div class="search-palette-input-row">
        <span class="search-palette-input-icon">🔍</span>
        <input type="text" id="searchPaletteInput" class="search-palette-input" placeholder="Search students by name…">
        <span class="search-palette-esc">esc</span>
      </div>
      <div id="searchPaletteList" class="search-palette-list"></div>
      <div class="search-palette-footer">
        <span>↑↓ navigate</span>
        <span>↵ open</span>
        <span>esc close</span>
      </div>
    </div>`);
  document.body.appendChild(palette);
  inputEl = palette.querySelector('#searchPaletteInput');
  listEl = palette.querySelector('#searchPaletteList');
  activeIdx = 0;
  renderList();
  ensureArchivedLoaded();   // fold graduated students into the pool (lazy, once)
  inputEl.focus();
  inputEl.addEventListener('input', () => { activeIdx = 0; renderList(); });
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closePalette(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx = Math.min(filtered.length - 1, activeIdx + 1); renderList(); ensureVisible(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); activeIdx = Math.max(0, activeIdx - 1); renderList(); ensureVisible(); }
    else if (e.key === 'Enter') { e.preventDefault(); goToSelected(); }
  });
  palette.addEventListener('click', (e) => { if (e.target === palette) closePalette(); });
}

function ensureVisible() {
  const active = listEl.querySelector('.search-result.active');
  if (active) active.scrollIntoView({ block: 'nearest' });
}

function closePalette() {
  if (!palette) return;
  palette.remove();
  palette = null;
  inputEl = null;
  listEl = null;
}

export function setupSearchPalette() {
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (palette) closePalette(); else openPalette();
    }
  });
}
